import { Constants, Mesh, MeshBuilder } from '@babylonjs/core';
import { BabylonjsModule } from '@modules/babylonjs/src/BabylonjsModule';
import { BaseSystem, SystemOperationDefinition, Zed } from '@modules/core';
import { createEntityFactory } from '@modules/standard';
import { World } from 'miniplex';

import { Entity as BaseEntity } from '..';
import { LayerMask } from '../../Constants';
import { TourdanjiSkyMaterialLight } from '../../graphic/material/TourdanjiSkyMaterialLight';

type Entity = BaseEntity;

export class SkySystem implements BaseSystem {
  static readonly INJECTABLE_TOKEN = Symbol.for('SkySystem');
  private readonly createEntity;
  private readonly skybox: Mesh;
  private readonly scene;
  private readonly skyDistance = 10; // Distance from camera

  constructor({
    [Zed.WORLD_TOKEN]: world,
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
  }: {
    [Zed.WORLD_TOKEN]: World<Entity>;
    [BabylonjsModule.INJECTABLE_TOKEN]: BabylonjsModule;
  }) {
    this.createEntity = createEntityFactory(world);
    this.scene = babylonjs.scene;
    
    // Full-screen quad for sky background (4 vertices instead of thousands)
    this.skybox = MeshBuilder.CreatePlane('SkyPlane', {
      size: 100, // Large enough to cover view at distance
      sideOrientation: Mesh.FRONTSIDE,
    });
    
    this.skybox.layerMask = LayerMask.Sky;
    this.skybox.isPickable = false;
    // 항상 제일 먼저 그린다.
    this.skybox.renderingGroupId = 0; 
    // 항상 카메라 정면.
    this.skybox.billboardMode = Mesh.BILLBOARDMODE_ALL;

    const tourdanjiSkyMaterial = new TourdanjiSkyMaterialLight('tourdanjiSkyLight', babylonjs.scene);
    tourdanjiSkyMaterial.horizonHeight = 0.2;
    tourdanjiSkyMaterial.backFaceCulling = false;
    tourdanjiSkyMaterial.disableDepthWrite = true;
    tourdanjiSkyMaterial.depthFunction = Constants.ALWAYS;
    this.skybox.material = tourdanjiSkyMaterial;
    
    this.createEntity({
      Skybox: {
        material: tourdanjiSkyMaterial,
      },
    });
  }

  private whenViewMatrixChanged = () => {
    const camera = this.scene.activeCamera;
    if (!camera) return;

    // 카메라 정면배치
    const forward = camera.getForwardRay().direction;
    const newPosition = camera.position.add(forward.scale(this.skyDistance));
    this.skybox.position.copyFrom(newPosition);
  };

  readonly operations: SystemOperationDefinition[] = [
    {
      group: 'viewMatrixChanged',
      run: this.whenViewMatrixChanged,
    },
  ];
}
